import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadPersonaPdf } from '@/entities/persona';
import { notificationService } from '@/lib/services/notification.service';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { track } from '@/shared/analytics';
import { downloadConversationPdf } from '../api/downloadConversationPdf';

interface PersonaInfo {
  id: string;
  firstName: string;
  segmentName: string;
}

interface UseInterviewDownloadOptions {
  persona: PersonaInfo | null;
  /** Chat/session id; the conversation PDF is rendered live from it. */
  contextId: string | null;
  workspaceId: string | null;
}

export function useInterviewDownload({
  persona,
  contextId,
  workspaceId,
}: UseInterviewDownloadOptions) {
  const { t } = useTranslation('audiences');

  // eslint-disable-next-line i18next/no-literal-string -- download filename segment
  const personaName = persona?.firstName || 'transcript';

  const [headlessDownloading, setHeadlessDownloading] = useState(false);
  const [headlessError, setHeadlessError] = useState<Error | null>(null);

  const downloadHeadless = useCallback(
    async (type: 'conversation' | 'persona') => {
      if (!workspaceId) throw new Error('No workspace selected');
      setHeadlessDownloading(true);
      setHeadlessError(null);
      const notifId = notificationService.showInfo(
        t('audienceInterview.useInterviewDownload.preparingYourDownload'),
        { persist: true },
      );
      try {
        if (type === 'persona') {
          if (!persona) throw new Error('No persona to export');
          const blob = await downloadPersonaPdf(persona.id, { workspaceId });
          // eslint-disable-next-line i18next/no-literal-string -- download filename segment
          const fileName = `${sanitizeDownloadFileName(persona.segmentName, 'persona')}.pdf`;
          await downloadFile({ blob, mimeType: 'application/pdf', fileName });
        } else {
          if (!contextId) throw new Error('No conversation to export');
          // Rendered live from the session by contextId: nothing persisted, and
          // the PDF always reflects the latest messages.
          const blob = await downloadConversationPdf(contextId, { workspaceId });
          // eslint-disable-next-line i18next/no-literal-string -- download filename segment
          const fileName = `${sanitizeDownloadFileName(personaName, 'conversation')}-transcript.pdf`;
          await downloadFile({ blob, mimeType: 'application/pdf', fileName });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to download export');
        setHeadlessError(error);
        notificationService.showError(
          type === 'persona'
            ? t('audienceInterview.useInterviewDownload.failedToDownloadPersona')
            : t('audienceInterview.useInterviewDownload.failedToDownloadConversation'),
        );
        throw error;
      } finally {
        notificationService.close(notifId);
        setHeadlessDownloading(false);
      }
    },
    [workspaceId, persona, contextId, personaName, t],
  );

  const download = useCallback(
    async (type: 'conversation' | 'persona') => {
      await downloadHeadless(type);
      track('document_downloaded', {
        document_type: type === 'conversation' ? 'audience_session' : 'persona',
        format: 'pdf',
        downloader: 'loc',
      });
    },
    [downloadHeadless],
  );

  return {
    download,
    isDownloading: headlessDownloading,
    error: headlessError,
  };
}
